import { useState, useEffect } from 'react';
import type { TUseFileUpload } from '../../types/file_uploader/use-file-upload';
import TEXT from './text.json';


export const useFileUpload = ({
  lang = 'en',
  allowedFormats,
  allowedSize,
  previouslyUploadedFile,
  error = '',
  multiUpload = false,
  onError,
  onUploadHandler,
  localization,
}: TUseFileUpload) => {
  const languageTexts = TEXT[lang as keyof typeof TEXT] || TEXT.en;
  const localizedTexts = { ...languageTexts, ...localization };

  const [invalid, setInvalid] = useState(false);
  const [errorMsg, setErrorMsg] = useState(error);
  const [uploadedFile, setUploadedFile] = useState<File | undefined>();
  const [uploadedFileObjects, setUploadedFileObjects] = useState<File[]>([]);
  const [uploadedFileInfo, setUploadedFileInfo] = useState<{ name: string; size?: number }>({ name: '' });
  const [uploadedFilesInfo, setUploadedFilesInfo] = useState<{ name: string; size?: number }[]>([]);

  const INTERNAL_ERROR_TYPES = {
    NO_FILE: 'noFile',
    SIZE: 'size',
    FORMAT: 'format',
  } as const;

  const displayAllowedFormats = (arrOfFormats: Array<string> | undefined) => {
    const COMMA_SEPARATED_FORMATS = arrOfFormats?.join(', ').toUpperCase();
    const FINAL_DISPLAY = COMMA_SEPARATED_FORMATS?.replace(/,([^,]*)$/, ` or $1`); // remove the last comma with 'or'

    return FINAL_DISPLAY;
  };

  const checkFileType = (fileName: string): 'image' | 'file' =>
    ['png', 'jpg', 'jpeg', 'webp'].some(ext => fileName.includes(ext)) ? 'image' : 'file';

  const fileUploadHandler = (e: any, file?: File) => {
    setInvalid(false);
    setErrorMsg('');
    const FILE = (e?.target?.files && e?.target?.files[0]) || file;
    const MAX_SIZE: any = allowedSize && allowedSize * 1024 * 1024;
    const fileExtension = FILE.name.split('.').pop() || '';
    const fileType = FILE?.type.split('/')[1];
    const fileFormats = [fileType, fileExtension, `.${fileExtension}`].filter(Boolean);
    const isAllowed = fileFormats.some(format => allowedFormats?.includes(format as string));

    if (!FILE) {
      setInvalid(true);
      setErrorMsg(localizedTexts.noFileUploaded);
      onError?.(INTERNAL_ERROR_TYPES.NO_FILE, localizedTexts.noFileUploaded, FILE);
      return;
    }

    if (FILE.size > MAX_SIZE) {
      const errMsg = localizedTexts.fileSize.replace('{{size}}', String(allowedSize));
      setInvalid(true);
      setErrorMsg(errMsg);
      onError?.(INTERNAL_ERROR_TYPES.SIZE, errMsg, FILE);
      return;
    }

    if (!isAllowed) {
      setInvalid(true);
      setErrorMsg(localizedTexts.notSupportedFormat);
      onError?.(INTERNAL_ERROR_TYPES.FORMAT, localizedTexts.notSupportedFormat, FILE);
      return;
    }

    if (multiUpload) {
      setUploadedFilesInfo(prev => [...prev, { name: FILE.name, size: FILE.size }]);
      setUploadedFileObjects(prev => {
        const updated = [...prev, FILE];
        return updated;
      });
      onUploadHandler?.([...uploadedFileObjects, FILE]);
    } else {
      onUploadHandler?.(FILE);
      setUploadedFile(FILE);
      setUploadedFileInfo({ name: FILE.name, size: FILE.size });
    }

    setInvalid(false);
    setErrorMsg('');
    if (e?.target) e.target.value = null;
  };

  const handleUploadAgain = () => {
    document.getElementById('file-upload-input')?.click();
  };

  const handleFileDelete = () => {
    setInvalid(false);
    setErrorMsg('');
    setUploadedFile(undefined);
    setUploadedFileInfo({ name: '' });
    onUploadHandler?.(undefined);
  };

  const handleMultiFileDelete = (index: number) => {
    const updated = uploadedFileObjects.filter((_, i) => i !== index);
    setUploadedFilesInfo(prev => prev.filter((_, i) => i !== index));
    setUploadedFileObjects(updated);
    onUploadHandler?.(updated);
  };

  useEffect(() => {
    if (!previouslyUploadedFile) return;
    if (Array.isArray(previouslyUploadedFile)) {
      setUploadedFilesInfo(previouslyUploadedFile.map(name => ({ name })));
    } else {
      setUploadedFileInfo({ name: previouslyUploadedFile });
    }
  }, [previouslyUploadedFile]);

  return {
    invalid,
    errorMsg,
    uploadedFile,
    uploadedFileInfo,
    uploadedFilesInfo,
    uploadedFileObjects,
    localizedTexts,
    fileUploadHandler,
    handleUploadAgain,
    handleFileDelete,
    handleMultiFileDelete,
    displayAllowedFormats,
    checkFileType,
  };
};
